import React, { useState, useCallback, forwardRef, lazy, Suspense, useRef, useEffect } from "react";
import { graphql, useStaticQuery } from "gatsby";
import PropTypes from "prop-types";
import PreviewBar from "../preview-bar";
import useClickOut from "../../hooks/use-click-out";
import useBlockScroll from "../../hooks/use-block-scroll";
import MainBenefits from "../main-benefits";
import BannerTopMenu from "../banner-top-menu";
import BannerPushNotification from "../elements/banner-push-notification";
import { hasEcommerce } from "../../utility/ecommerce";
import HeaderTop from "./new-header-top";
import NewNavigation from "./new-navigation";

const LazySearch = lazy(() => import("../search"));

const NewHeader = forwardRef(
  (
    {
      siteTitle,
      navUp,
      toggleHamburger,
      mobileMenuVisible,
      bannerTop,
      bannerRef,
      hideMainBenefits,
      mainBenefits,
      searchVisible,
      setSearchVisible,
      bannerTopMenuHeight,
      hamburger,
    },
    ref
  ) => {
    const [search, setSearch] = useState(searchVisible);
    const toggleSearch = useCallback(() => {
      setSearch((value) => !value);
      setSearchVisible((value) => !value);
    }, [setSearchVisible]);
    const searchRef = useClickOut(search, setSearch);
    useBlockScroll(search);

    const {
      labelManagerLabels,
      config,
      menuConfig,
      assistanceMenuFiltered: { selectName },
      newTopLeftMenu,
      newTopRightMenu,
    } = useStaticQuery(graphql`
      {
        labelManagerLabels: labelManagerLabels {
          search
          menu
          support
        }
        config: liferayGeneralConfiguration {
          shopLink
        }
        menuConfig: liferayMenuConfiguration {
          ciamActive
          languageActive
          searchActive
        }
        assistanceMenuFiltered {
          selectNameFieldSet {
            selectName
            page {
              ... on LiferayPage {
                id
                liferayFields {
                  title
                }
                slug
              }
              ... on LiferayPageExternalLink {
                id
                liferayFields {
                  title
                }
                externalLink
                target
              }
            }
          }
        }
        newTopLeftMenu {
          name
          navigationMenuItems {
            useCustomName
            name
            content {
              ... on LiferayPage {
                id
                slug
                target
                liferayFields {
                  title
                }
              }
              ... on LiferayPageExternalLink {
                id
                liferayFields {
                  title
                }
                target
                externalLink
                icon
                alternativeIcon {
                  alternativeIcon {
                    value {
                      description
                    }
                    node {
                      publicURL
                    }
                  }
                  height
                  width
                }
              }
              ... on LiferayPageLine {
                id
                slug
                liferayFields {
                  title
                }
              }
              ... on LiferayPageCrossLine {
                id
                slug
                liferayFields {
                  title
                }
              }
            }
          }
        }
        newTopRightMenu {
          name
          navigationMenuItems {
            useCustomName
            name
            content {
              __typename
              ... on LiferayPage {
                id
                slug
                target
                liferayFields {
                  title
                }
              }
              ... on LiferayPageExternalLink {
                id
                liferayFields {
                  title
                }
                target
                externalLink
              }
              ... on LiferayPageLine {
                id
                slug
                liferayFields {
                  title
                }
              }
              ... on LiferayPageCrossLine {
                id
                slug
                liferayFields {
                  title
                }
              }
              ... on LiferayBannerMenu {
                newTitle: title
                image {
                  value {
                    description
                  }
                  node {
                    gatsbyImageData(layout: FULL_WIDTH)
                    publicURL
                  }
                }
                ctaLabel
                ctaLink
                ctaTarget
                ctaType
                abstract
              }
              ... on LiferayAcBannerMenu {
                newTitle: title
                cta {
                  label
                  slug
                  target
                }
                image {
                  value {
                    description
                  }
                  node {
                    gatsbyImageData(layout: FULL_WIDTH)
                    publicURL
                  }
                }
              }
            }
            navigationMenuItems {
              useCustomName
              name
              content {
                ... on LiferayPage {
                  id
                  liferayFields {
                    title
                  }
                  slug
                  target
                  menuImage {
                    node {
                      gatsbyImageData(width: 500, layout: CONSTRAINED)
                    }
                    value {
                      description
                    }
                  }
                }
                ... on LiferayPageCrossLine {
                  id
                  liferayFields {
                    title
                  }
                  slug
                  menuImage {
                    node {
                      gatsbyImageData(width: 500, layout: CONSTRAINED)
                    }
                    value {
                      description
                    }
                  }
                }
                ... on LiferayPageExternalLink {
                  id
                  liferayFields {
                    title
                  }
                  externalLink
                  target
                  menuImage {
                    node {
                      gatsbyImageData(width: 500, layout: CONSTRAINED)
                    }
                    value {
                      description
                    }
                  }
                }
                ... on LiferayPageLine {
                  id
                  liferayFields {
                    title
                  }
                  pageNameMenu
                  slug
                  lineImageFieldSet {
                    lineImage {
                      node {
                        gatsbyImageData(width: 500, layout: CONSTRAINED)
                      }
                    }
                    altImage
                  }
                  allBabelProdotto {
                    id
                  }
                }
              }
              navigationMenuItems {
                useCustomName
                name
                content {
                  ... on LiferayPage {
                    id
                    liferayFields {
                      title
                    }
                    slug
                    target
                  }
                  ... on LiferayPageCrossLine {
                    id
                    liferayFields {
                      title
                    }
                    slug
                  }
                  ... on LiferayPageExternalLink {
                    id
                    liferayFields {
                      title
                    }
                    externalLink
                    target
                  }
                  ... on LiferayPageLine {
                    id
                    liferayFields {
                      title
                    }
                    pageNameMenu
                    slug
                    allBabelProdotto {
                      id
                    }
                  }
                }
              }
            }
          }
        }
      }
    `);

    const internalRef = useRef();
    useEffect(() => {
      const refEl = internalRef.current;
      if (refEl) {
        const resizeListener = () => {
          const { height } = refEl.getBoundingClientRect();
          const event = new CustomEvent("header-resize", {
            detail: {
              height,
            },
          });
          document.dispatchEvent(event);
        };
        setTimeout(() => resizeListener(), 100);
        window.addEventListener("resize", resizeListener);
        return () => window.removeEventListener("resize", resizeListener);
      }
    }, []);

    return (
      <div ref={ref} className="header">
        {process.env.EDIT_MODE === "true" && <PreviewBar />}
        {bannerTop && (
          <BannerPushNotification
            sleekNoteId="sn-785b13d4-ee9e-4fc6-acfe-e7bcc50e61dd"
            topMenu={true}
          >
            <BannerTopMenu ref={bannerRef} data={bannerTop} />
          </BannerPushNotification>
        )}
        <HeaderTop
          mobileMenuVisible={mobileMenuVisible}
          toggleHamburger={toggleHamburger}
          labelManagerLabels={labelManagerLabels}
          siteTitle={siteTitle}
          hasEcommerce={hasEcommerce}
          config={config}
          search={search}
          toggleSearch={toggleSearch}
          newTopLeftMenu={newTopLeftMenu}
          newTopRightMenu={newTopRightMenu}
          key={navUp}
          menuConfig={menuConfig}
        />
        <div className="container-fluid">
          <div className="row">
            <div className="col-12 px-lg-0">
              <div ref={internalRef} className="d-flex justify-content-between align-items-center">
                <NewNavigation
                  navUp={navUp}
                  mobileMenuVisible={mobileMenuVisible}
                  key={mobileMenuVisible}
                  selectName={selectName}
                  newTopLeftMenu={newTopLeftMenu}
                  newTopRightMenu={newTopRightMenu}
                  labelManagerLabels={labelManagerLabels}
                  bannerTop={bannerTop}
                  bannerTopMenuHeight={bannerTopMenuHeight}
                  menuConfig={menuConfig}
                  hamburger={hamburger}
                >
                  <button
                    className="hamburger open"
                    aria-label={labelManagerLabels.menu}
                    onClick={toggleHamburger}
                  >
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                  </button>
                </NewNavigation>
              </div>
            </div>
          </div>
        </div>
        {hideMainBenefits !== "yes" && <MainBenefits data={mainBenefits} />}
        {search && (
          <Suspense>
            <LazySearch ref={searchRef} open={search} toggleSearch={toggleSearch} />
          </Suspense>
        )}
      </div>
    );
  }
);

NewHeader.propTypes = {
  siteTitle: PropTypes.string,
};

NewHeader.defaultProps = {
  siteTitle: ``,
};

export default NewHeader;
